import React, { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { ModalHeader } from '../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { ModalFooter } from '../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../../CommonComponents/OPL/Modal/Modal';
import { useStockOptionsContext } from '../../context/useStockOptionsContext';
import { useSaveAccountMutation } from '../../api/useSaveAccountMutation';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { FormValues, validation } from './EditAccountModal.meta';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { AccountDTO } from '../../../../Profile/Accounts/api/useAccountsQuery';
import { ModalBody } from '../../../../CommonComponents/OPL/Modal/ModalBody';
import { DeleteAccountModal } from '../DeleteAccountModal/DeleteAccountModal';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';

interface Props {
  clientId: string;
  account: AccountDTO;
  onClose(): void;
}

export const EditAccountModal = ({ clientId, account, onClose }: Props) => {
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);

  const { options } = useStockOptionsContext();

  const formBag = useForm<FormValues>({
    defaultValues: {
      name: account.AccountName || '',
      owner: account.AccountOwner || '',
      assetClass: account.StockOptionAssetClass,
      earmarks: (account.AccountEarmark || []).map(({ Tooltip, Type }) => ({
        label: Tooltip,
        value: Type,
      })),
      ownerModel: account.OwnerModel,
    },
    resolver: yupResolver(validation),
  });

  const {
    mutateAsync: saveAccount,
    isLoading: isSavingAccount,
    error: saveAccountError,
  } = useSaveAccountMutation({ clientId });

  const onSubmit = formBag.handleSubmit(async values => {
    try {
      const newAccount: AccountDTO = {
        ...account,
        AccountName: values.name,
        AccountOwner: values.owner,
        OwnerModel: values.ownerModel,
        StockOptionAssetClass: values.assetClass,
        AccountEarmark: values.earmarks.map(({ value, label }) => ({
          Type: value,
          Tooltip: label,
        })),
      };

      await saveAccount({ clientId, account: newAccount });

      toast.success('Account saved successfully.');

      onClose();
    } catch (error) {
      console.error(error);
    }
  });

  const watchedOwnerModel = useWatch({
    name: 'ownerModel',
    control: formBag.control,
  });

  const ownerOptions = [
    {
      label: 'People',
      options: options.OwnerOptions.People.map(owner => ({
        value: owner.Id,
        label: owner.Name,
      })),
    },
    {
      label: 'Trusts',
      options: options.OwnerOptions.Trusts.map(owner => ({
        value: owner.Id,
        label: owner.Name,
      })),
    },
  ];

  return (
    <>
      <Modal
        isOpen
        onRequestClose={onClose}
        shouldCloseOnOverlayClick={false}
        aria={{ labelledby: 'edit-account--modal-header' }}
        isHidden={isDeleteAccountModalOpen}
      >
        <ModalHeader as="h2" id="edit-account--modal-header">
          Edit Account
        </ModalHeader>
        <ModalCloseButton onClick={onClose} />

        {saveAccountError && (
          <Alert message={saveAccountError.message} variant="error" mb="16" mx="24" />
        )}

        <form id="edit-account--form" onSubmit={onSubmit}>
          <ModalBody>
            <Box display="flex" flexDirection="column" css="gap: 16px">
              <Input
                {...formBag.register('name')}
                label="Company Name"
                maxLength={144}
                formVariant={formBag.formState.errors.name ? 'error' : undefined}
                helperText={formBag.formState.errors.name?.message}
              />

              <Controller
                name="owner"
                control={formBag.control}
                render={({ field: { value, onChange, ref } }) => (
                  <Select
                    innerRef={ref}
                    label="Owner"
                    value={ownerOptions
                      .map(({ options }) => options)
                      .flat()
                      .find(option => option.label === value)}
                    options={ownerOptions}
                    onChange={opt => {
                      onChange(opt?.label);
                      if (opt) {
                        const selectedOwnerOption = Object.values(options.OwnerOptions)
                          .map(items => items)
                          .flat()
                          .find(option => option.Name === opt.label);
                        if (selectedOwnerOption) {
                          formBag.setValue('ownerModel', [
                            {
                              ...watchedOwnerModel[0],
                              Id: opt.value,
                              Name: opt.label,
                              OwnerType: selectedOwnerOption.OwnerType,
                              Percentage: 1,
                            },
                          ]);
                        }
                      }
                    }}
                    formVariant={formBag.formState.errors.owner ? 'error' : undefined}
                    helperText={formBag.formState.errors.owner?.message}
                    isPortal
                  />
                )}
              />

              <Controller
                name="assetClass"
                control={formBag.control}
                render={({ field: { value, onChange, ref } }) => (
                  <Select
                    isClearable
                    innerRef={ref}
                    label="Asset Class"
                    value={options.AssetClassOptions.find(option => option.value === value) || null}
                    options={options.AssetClassOptions}
                    onChange={option => onChange(option?.value ?? null)}
                    isPortal
                  />
                )}
              />

              <Controller
                control={formBag.control}
                name="earmarks"
                render={({ field: { value, onChange } }) => {
                  return (
                    <Select
                      isMulti
                      label="Account Earmark"
                      value={value}
                      options={options.EarmarkOptions}
                      onChange={earmarks => onChange(earmarks)}
                      isPortal
                    />
                  );
                }}
              />
            </Box>
          </ModalBody>
        </form>

        <ModalFooter>
          <Button
            variant="errorSecondary"
            onClick={() => setIsDeleteAccountModalOpen(true)}
            mr="auto"
          >
            Delete
          </Button>

          <Button variant="subtleInactive" onClick={onClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            type="submit"
            form="edit-account--form"
            isLoading={isSavingAccount}
            ml="8"
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>

      {isDeleteAccountModalOpen && (
        <DeleteAccountModal
          clientId={clientId}
          account={account}
          onClose={() => setIsDeleteAccountModalOpen(false)}
          onSuccess={() => {
            onClose();
            setIsDeleteAccountModalOpen(false);
          }}
        />
      )}
    </>
  );
};
